import { FC, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { OutlinedInput, InputLabel, MenuItem, FormControl, ListItemText, Select, Checkbox, Tooltip, Autocomplete, TextField, Box } from '@mui/material';
import { CountriesContext } from '@providers/Countries';
import { GlobeContext } from '@providers/Globe';
import { Close } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import * as types from '@models/types';
import { getHubAccess } from '@helpers/utils';
import './Input.scss';

type SelectSingleProps = {
    title: string;
    name?: string;
    selectedItem: string|number|null;
    data?: any[];
    handleOpen?: () => void;
    handleChange: (e: any) => void;
    handleClose?: () => void;
    style?:any
};

type SelectMultipleProps = {
    title: string;
    name?: string;
    selectedItems: any[];
    data?: any[];
    handleOpen?: () => void;
    handleChange: (e: any, latestSelected: any, latestParentSelected: any, data:any[]) => void;
    handleClose?: () => void;
    displayTooltip?: boolean;
    prefixTooltip?: string;
    style?: any;
    disabled?: boolean;
};

type SelectCountryProps = {
    setSelectedProfile: React.Dispatch<React.SetStateAction<types.SelectedProfileTypes>>
};

export const SelectSingle: FC<SelectSingleProps> = ({ title, name, selectedItem, data, handleOpen, handleChange, handleClose, style }) => {
    const randomLabelId = `labelId${Math.floor(Math.random() * 1000000)}`

    const getItemId = useCallback((item: any) => {
        return item.parentId && !item.id ? item.parentId : item.id;
    }, []);

    const getItemTitle = useCallback((item: any) => {
        return item.parentId && !item.id ? item.parentTitle : item.title;
    }, []);

    const getItemClass = useCallback((item: any) => {
        return "singleSelect " + (item.parentId && item.id ?
            "childItem"
            :
            (item.parentId && !item.id ? "parentItem" : ""));
    }, []);

    return data && data.length ? <FormControl fullWidth>
        {selectedItem ? <button className="btn select-reset" onClick={() => handleChange({ target: "reset", name })}><Close /></button> : <></>}
        <InputLabel id={randomLabelId}>Select {title}</InputLabel>
        <Select
            name={name}
            labelId={randomLabelId}
            value={selectedItem || ""}
            label={`Select ${title}`}
            onOpen={handleOpen}
            onChange={handleChange}
            onClose={handleClose}
            style={style}
        >
            {data.map(item =>
                <MenuItem key={getItemId(item)} value={item && item.id ? item.id : null} className={getItemClass(item)}>
                    {getItemTitle(item)}
                </MenuItem>)
            }
        </Select>
    </FormControl>:null;
};

export const SelectMultiple: FC<SelectMultipleProps> = ({ title, name, selectedItems, data, handleOpen, handleChange, handleClose, displayTooltip, prefixTooltip, style, disabled }) => {
    const randomLabelId = `labelId${Math.floor(Math.random() * 1000000)}`
    const unclassified = data?.find((item: any) => item.title === "Unclassified")

    const getItemId = useCallback((item: any) => {
        return item.parentId && !item.id ? item.parentId : item.id;
    }, []);

    const getItemTitle = useCallback((item: any) => {
        return item.parentId && !item.id ? item.parentTitle : item.title;
    }, []);

    const getItemClass = useCallback((item: any, isParentItemSelected?: boolean) => {
        return item.parentId && item.id ?
            "childItem"
            :
            (item.parentId && !item.id ? "parentItem" + ((selectedItems.length > 0 && data && data.length > 0 && selectedItems.filter(e => e.parentTitle === item.parentTitle).length === data.filter((e: any) => e.title && e.parentTitle === item.parentTitle).length) ? " Mui-selected" : "") : "");
    }, [data, selectedItems]);

    const isItemChecked = useCallback((item:any) => {
        return (item.parentId && !item.id) ? ((selectedItems.length > 0 && data && data.length > 0 && selectedItems.filter(e => e.parentTitle === item.parentTitle).length === data.filter((e: any) => e.title && e.parentTitle === item.parentTitle).length) ? true : false)
                                                : typeof selectedItems.find(e => e.title === item.title) !== "undefined";
    }, [data, selectedItems]);

    const isSelectAllChecked = useCallback(() => {
        if (data && data.length > 0 && data[0].parentTitle) {
            return selectedItems.length === data.filter((e: any) => e.id && e.parentId).length;
        }
        else {
            return data && selectedItems.length === data.length;
        }    
    }, [data, selectedItems]);

    return data && data.length ? <FormControl fullWidth>
        {selectedItems.length ? <button className="btn select-reset" onClick={() => handleChange({ target: { value: "reset", name } }, "reset", null, data)}><Close /></button> : <></>}
        <InputLabel id={randomLabelId} className={disabled ? "selectDisabled" : ""}>{disabled ? "All " + title : "Select " + title }</InputLabel>
        <Select
            name={name}
            labelId={randomLabelId}
            multiple
            className="w-100 d-flex"
            value={selectedItems}
            onOpen={handleOpen}
            style={style}
            onChange={(e: any, t: any) => {
                const latestSelectedTitle = typeof t.props.value === "string" ? t.props.value : t.props.value.title;
                const latestSelectedParentTitle = typeof t.props.value !== "string" && t.props.value.parentTitle ? t.props.value.parentTitle : null;
                handleChange(e, latestSelectedTitle, latestSelectedParentTitle, data)
            }
            }
            onClose={handleClose}
            input={<OutlinedInput label={`Select ${title}`} />}
            renderValue={(selected: any) => {
                const selectedValue = selected.map((e: any) => e.title)
                return selectedValue.join(', ')
            }
            }
            MenuProps={{ variant: "menu" }}
            disabled={disabled}
        >
            {(isSelectAllChecked()) ? <MenuItem key={0} value="reset">
                <Checkbox checked={true} name="reset" />
                <ListItemText primary="Unselect all" />
            </MenuItem> : <MenuItem key={0} value="all">
                <Checkbox checked={false} name="all" />
                <ListItemText primary="Select all" />
            </MenuItem>}
            {unclassified ? <Divider /> : null}
            {unclassified ? <MenuItem key={unclassified.id} value={unclassified}>
                <Checkbox checked={typeof selectedItems.find(e => e.title === unclassified.title) !== "undefined"} name={unclassified.title} />
                <Tooltip key={unclassified.id} title={prefixTooltip + unclassified.tooltip} placement="top-end" arrow followCursor={true} disableHoverListener={!displayTooltip}>
                    <ListItemText primary={unclassified.title} />
                </Tooltip>
            </MenuItem> : null}
            {unclassified ? <Divider /> : null}
            {data.map((item: any) => (
                (item.title !== "Unclassified") &&
                <MenuItem key={getItemId(item)} value={item} className={getItemClass(item)}>
                    <Checkbox checked={isItemChecked(item)} name={getItemTitle(item)} />
                    <Tooltip key={getItemId(item)} title={prefixTooltip + item.tooltip} placement="top-end" arrow followCursor={true} disableHoverListener={!displayTooltip}>
                        <ListItemText primary={getItemTitle(item)} />
                    </Tooltip>
                </MenuItem>)
            )}
        </Select>
    </FormControl> : null;
};

export const SelectCountry: FC<SelectCountryProps> = ({ setSelectedProfile }) => {
    const { countryList, countryDetails } = useContext(CountriesContext);
    const { selectedCountry, setSelectedCountry } = useContext(GlobeContext);
    const [filteredCountries, setFilteredCountries] = useState<types.CountryType[]>([]);

    useEffect(() => {
        if (countryList && countryList.length > 0 && filteredCountries.length === 0) {
            var hubAccesses = getHubAccess();
            if (!hubAccesses || (hubAccesses && hubAccesses.length === 0))
                return;

            var tempCountryList = countryList.filter(value => { return hubAccesses.find((h: any) => h.hubId === value.id) });
            setFilteredCountries(tempCountryList);
        }
    }, [countryList, filteredCountries]);

    const countryData = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code === selectedCountry) || null
        );
    }, [selectedCountry, countryDetails]);

    const handleChange = (event: any, newValue: types.CountryType) => {
        (setSelectedCountry && newValue) && setSelectedCountry(newValue.countryCode)

        setSelectedProfile((prevState: any) => ({
            ...prevState,
            provinces: []
        }));
    }
    return filteredCountries && filteredCountries.length ? <Autocomplete
        id="countriesDDL" 
        value={countryData}
        options={filteredCountries}
        autoHighlight fullWidth
        onChange={handleChange}
        getOptionLabel={(option) => option.country}
        isOptionEqualToValue={(option, value) => option.country === value.country}
        renderOption={(props, option) => (
            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                {option.country} ({option.countryCode})
            </Box>
        )}
        renderInput={(params) => (
            <TextField
                {...params}
                variant="standard"
                inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                }}
            />
        )}
    /> : null;
};
